"use client";

/**
 * ActBody — submitter + publish time + contract/op + args + description
 *
 * Figma 5462:53045 / 5462:53106 / 5462:53169 body block (all variants).
 * Uses Jalali date formatting for publish time.
 */

import { useTranslations } from "next-intl";
import { WalletAddress } from "@/components/core/WalletAddress";
import { ContractAddress } from "@/contract/addresses";
import { useDateFormatter } from "@/lib/i18n/date";
import { DescriptionMarkdown } from "./DescriptionMarkdown";
import { MethodArgsDisplay } from "./MethodArgsDisplay";

export function contractDisplayName(address: string): string {
  const lower = address.toLowerCase();
  if (lower === ContractAddress.GATEWAY.toLowerCase()) return "Gateway";
  // Guarded on non-empty: these tolerate a missing env and collapse to "",
  // which would otherwise match nothing useful and mislabel an unrelated
  // proposal whose target happens to be absent.
  if (
    ContractAddress.GATEWAY_V2.length > 0 &&
    lower === ContractAddress.GATEWAY_V2.toLowerCase()
  )
    return "Gateway V2";
  if (
    ContractAddress.GATEWAY_V3.length > 0 &&
    lower === ContractAddress.GATEWAY_V3.toLowerCase()
  )
    return "Gateway V3";
  if (
    ContractAddress.GATEWAY_UPGRADE_CONTROLLER.length > 0 &&
    lower === ContractAddress.GATEWAY_UPGRADE_CONTROLLER.toLowerCase()
  )
    return "Gateway Upgrade Controller";
  if (lower === ContractAddress.STAKE.toLowerCase()) return "Stake";
  if (lower === ContractAddress.WRAPPER.toLowerCase()) return "Wrapper";
  if (lower === ContractAddress.UVM.toLowerCase()) return "UVM";
  if (lower === ContractAddress.DAO_SAFE_VAULT.toLowerCase())
    return "DaoSafeVault";
  return address;
}

const METHOD_LABELS: Record<string, string> = {
  "withdrawWithDao(uint256,address)": "Withdraw Balance By Dao",
  "setCalculator(address)": "Set Calculator",
  "withdrawFeeByDao(uint256,address)": "Withdraw Balance By Dao",
  "setFeePercent(uint256)": "Set Fee Percent",
  "changeCollectionStatus(address,bool,bool)": "Change Collection Status",
  "addOrUpdateCollection(address,tuple,tuple[])": "Add Or Update Collection",
  "withdrawERC20(address,address,uint256)": "Withdraw ERC20",
  "withdrawERC721(address,address,uint256)": "Withdraw ERC721",
  "withdrawETH(address,uint256)": "Withdraw ETH",
  "calculateWeek()": "Calculate Week",
  "userWithdrawWeekUVM(uint256)": "Withdraw Weekly Income (UVM)",
  "transfer(address,uint256)": "Transfer",
  "confirmBinding(bytes32)": "Confirm Gateway Binding",
  "confirmUpgrade(bytes32)": "Confirm Gateway Upgrade",
};

function methodDisplayLabel(sig: string): string {
  return METHOD_LABELS[sig] ?? sig;
}

interface ActBodyProps {
  submitterAddress: string;
  submitterNickname: string | null;
  createdAt: string;
  contractAddress: string;
  functionSignature: string;
  inputs: unknown[];
  description: string;
}

export function ActBody({
  submitterAddress,
  submitterNickname,
  createdAt,
  contractAddress,
  functionSignature,
  inputs,
  description,
}: ActBodyProps) {
  const t = useTranslations("dao");
  const df = useDateFormatter();
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <span className="text-xs text-text-secondary">
          {t("proposals.detail.submittedBy")}
        </span>
        <div className="flex min-w-0 items-center gap-2">
          <WalletAddress address={submitterAddress} size={16} canCopy />
          {submitterNickname && (
            <span className="min-w-0 truncate text-sm text-text-primary">
              {submitterNickname}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs text-text-secondary">
          {t("proposals.detail.publishedAt")}
        </span>
        <span className="text-sm text-text-primary">
          {df.dateTime(createdAt)}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs text-text-secondary">
          {t("proposals.detail.contractType")}
        </span>
        <span className="text-sm text-text-primary">
          {contractDisplayName(contractAddress)}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs text-text-secondary">
          {t("proposals.detail.operation")}
        </span>
        <span className="text-sm text-text-primary">
          {methodDisplayLabel(functionSignature)}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <MethodArgsDisplay
          functionSignature={functionSignature}
          inputs={inputs}
        />
      </div>

      <div className="flex flex-col gap-2">
        <DescriptionMarkdown text={description} />
      </div>
    </div>
  );
}
